import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import {
  FilmIcon,
  MagnifyingGlassIcon,
  PlayCircleIcon,
  LinkIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useLanguage } from '../../contexts/LanguageContext';
import PageHeader from '../ui/PageHeader';
import EmptyState from '../ui/EmptyState';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import Input from '../ui/Input';
import { ListPageSkeleton } from '../ui/PageSkeleton';
import { getApiErrorMessage } from '../../utils/apiError';
import VideoPlayer, { resolveMediaUrl } from './VideoPlayer';
import { useRequestedVideo } from './useRequestedVideo';

function categoryLabel(t, category) {
  return t.videoHub?.categories?.[category] || category;
}

function VideoCard({ video, onPlay, t }) {
  const thumb = resolveMediaUrl(video.thumbnail_url || video.playback?.thumbnail_url);
  return (
    <motion.button
      type="button"
      onClick={() => onPlay(video)}
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="group card-premium interactive-lift flex h-full flex-col overflow-hidden text-left"
    >
      <div className="relative aspect-video overflow-hidden bg-gradient-to-br from-orange-100 via-amber-50 to-orange-200   ">
        {thumb ? (
          <img src={thumb} alt="" className="h-full w-full object-cover transition duration-500 group-hover:scale-105" />
        ) : (
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(249,115,22,0.35),transparent_55%)]" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-secondary-900/40 via-transparent to-transparent" />
        <div className="absolute inset-0 grid place-items-center">
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-white/90 text-orange-600 shadow-glow ring-1 ring-orange-200/80 transition group-hover:scale-110 sm:h-14 sm:w-14   ">
            <PlayCircleIcon className="h-8 w-8 sm:h-9 sm:w-9" />
          </span>
        </div>
        {video.is_featured ? (
          <span className="absolute left-3 top-3 rounded-full bg-orange-500/90 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-white">
            {t.videoHub.featured}
          </span>
        ) : null}
      </div>
      <div className="flex flex-1 flex-col gap-2 p-4 sm:p-5">
        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-orange-500">
          {categoryLabel(t, video.category)}
        </p>
        <h3 className="text-base font-bold leading-snug break-words text-secondary-900  sm:text-[1.05rem]">
          {video.title}
        </h3>
        {video.description ? (
          <p className="line-clamp-2 text-sm leading-relaxed text-secondary-600 ">
            {video.description}
          </p>
        ) : null}
      </div>
    </motion.button>
  );
}

function VideoHub({ audience = 'client' }) {
  const { t, formatMessage } = useLanguage();
  const vh = t.videoHub;
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('all');
  const [activeVideo, setActiveVideo] = useState(null);
  const endpoint = audience === 'vendor' ? '/api/vendor/videos' : '/api/client/videos';

  const fetchVideos = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(endpoint, { withCredentials: true });
      setVideos(response.data.videos || []);
    } catch (error) {
      toast.error(getApiErrorMessage(error, t, formatMessage, vh.loadError));
    } finally {
      setLoading(false);
    }
  }, [endpoint, formatMessage, t, vh.loadError]);

  useEffect(() => {
    fetchVideos();
  }, [fetchVideos]);

  const categories = useMemo(() => {
    const values = new Set(videos.map((video) => video.category).filter(Boolean));
    return ['all', ...Array.from(values)];
  }, [videos]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return videos.filter((video) => {
      if (category !== 'all' && video.category !== category) return false;
      if (!needle) return true;
      const haystack = `${video.title} ${video.description || ''}`.toLowerCase();
      return haystack.includes(needle);
    });
  }, [videos, query, category]);

  const featured = filtered.find((video) => video.is_featured) || filtered[0] || null;

  const openVideo = useCallback(async (video) => {
    setActiveVideo(video);
    try {
      await axios.post(`${endpoint}/${video.id}/view`, {}, { withCredentials: true });
    } catch {
      // Playback still works if the view counter is unavailable.
    }
  }, [endpoint]);

  const onMissingVideo = useCallback(() => {
    toast.error(vh.videoUnavailable);
  }, [vh.videoUnavailable]);

  useRequestedVideo({
    items: videos,
    loading,
    onOpen: openVideo,
    onMissing: onMissingVideo,
  });

  if (loading) {
    return <ListPageSkeleton />;
  }

  return (
    <div className="space-y-5 sm:space-y-8">
      <PageHeader
        icon={FilmIcon}
        eyebrow={vh.eyebrow}
        title={vh.title}
        subtitle={audience === 'vendor' ? vh.vendorSubtitle : vh.clientSubtitle}
      />

      {videos.length === 0 ? (
        <EmptyState
          icon={PlayCircleIcon}
          title={vh.emptyTitle}
          description={vh.emptyDescription}
        />
      ) : (
        <>
          {featured ? (
            <section className="card-premium overflow-hidden p-3 sm:p-6">
              <div className="mb-3 flex items-center gap-2 text-orange-600 sm:mb-4 ">
                <SparklesIcon className="h-5 w-5 shrink-0" />
                <p className="text-sm font-semibold tracking-wide">{vh.nowPlaying}</p>
              </div>
              <VideoPlayer video={featured} />
              <div className="mt-4 space-y-2 sm:mt-5">
                <h3 className="text-lg font-bold break-words text-secondary-900 sm:text-xl ">{featured.title}</h3>
                {featured.description ? (
                  <p className="max-w-3xl text-sm leading-relaxed text-secondary-600 ">
                    {featured.description}
                  </p>
                ) : null}
                <Button className="mt-2 w-full sm:w-auto" onClick={() => openVideo(featured)} icon={PlayCircleIcon}>
                  {vh.watchNow}
                </Button>
              </div>
            </section>
          ) : null}

          <div className="flex min-w-0 flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="relative min-w-0 w-full flex-1">
              <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-secondary-400" />
              <Input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={vh.searchPlaceholder}
                className="min-h-[44px] pl-9"
                aria-label={vh.searchPlaceholder}
              />
            </div>
            <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1 [-ms-overflow-style:none] [scrollbar-width:none] sm:flex-wrap sm:overflow-visible [&::-webkit-scrollbar]:hidden">
              {categories.map((value) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setCategory(value)}
                  className={`min-h-[40px] shrink-0 rounded-full px-3 py-1.5 text-xs font-semibold transition ${
                    category === value
                      ? 'bg-orange-500 text-white shadow-glow'
                      : 'bg-white/70 text-secondary-600 ring-1 ring-secondary-200/70 hover:bg-white   '
                  }`}
                >
                  {value === 'all' ? vh.allCategories : categoryLabel(t, value)}
                </button>
              ))}
            </div>
          </div>

          {filtered.length === 0 ? (
            <EmptyState
              compact
              icon={MagnifyingGlassIcon}
              title={vh.noResultsTitle}
              description={vh.noResultsDescription}
            />
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {filtered.map((video) => (
                <VideoCard key={video.id} video={video} onPlay={openVideo} t={t} />
              ))}
            </div>
          )}
        </>
      )}

      <Modal
        isOpen={Boolean(activeVideo)}
        onClose={() => setActiveVideo(null)}
        title={activeVideo?.title}
        size="xl"
      >
        {activeVideo ? (
          <div className="min-w-0 space-y-4">
            <VideoPlayer video={activeVideo} autoPlay />
            {activeVideo.description ? (
              <p className="text-sm leading-relaxed text-secondary-600 ">
                {activeVideo.description}
              </p>
            ) : null}
            {activeVideo.links?.length ? (
              <div className="space-y-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-secondary-500">
                  {vh.relatedLinks}
                </p>
                <div className="flex flex-wrap gap-2">
                  {activeVideo.links.map((link) => (
                    <a
                      key={`${link.label}-${link.url}`}
                      href={link.url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex min-h-[40px] max-w-full items-center gap-1.5 rounded-full bg-orange-50 px-3 py-1.5 text-sm font-medium text-orange-700 ring-1 ring-orange-200 hover:bg-orange-100   "
                    >
                      <LinkIcon className="h-4 w-4 shrink-0" />
                      <span className="truncate">{link.label}</span>
                    </a>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        ) : null}
      </Modal>
    </div>
  );
}

export default VideoHub;
